import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomToastrComponent } from '../components/custom-toastr/custom-toastr.component';

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private readonly _appRef = inject(ApplicationRef);
  private readonly _environmentInjector = inject(EnvironmentInjector);
  private _activeToasts: ComponentRef<CustomToastrComponent>[] = [];

  constructor(private _translate: TranslateService) {}

  private _showToast(
    type: 'success' | 'warning' | 'info' | 'danger' | 'confirm',
    {
      message,
      title,
      skipTranslation,
      actionLabel,
      onAction,
      cancelLabel,
      persistentAction,
      presentation,
      requestPassword,
      typeColor,
    }: IFeedbackMessage
  ) {
    const translatedMessage = message
      ? skipTranslation
        ? message
        : this._translate.instant(message)
      : '';
    const translatedTitle = title
      ? skipTranslation
        ? title
        : this._translate.instant(title)
      : undefined;
    const translatedActionLabel = actionLabel
      ? skipTranslation
        ? actionLabel
        : this._translate.instant(actionLabel)
      : undefined;

    // Crear el componente dinámicamente
    const toastrComponentRef = createComponent(CustomToastrComponent, {
      environmentInjector: this._environmentInjector,
    });

    // Configurar las propiedades del componente
    const instance = toastrComponentRef.instance;
    instance.type = type;
    instance.title = translatedTitle;
    instance.message = translatedMessage;
    instance.actionLabel = translatedActionLabel;
    instance.cancelLabel = cancelLabel;
    instance.autoclose = !persistentAction;
    instance.timeout = persistentAction ? 0 : 3000;
    instance.presentation = presentation ?? 'toast';
    instance.requestPassword = requestPassword ?? false;
    instance.typeColor = typeColor ?? 'negative';

    // Configurar eventos
    instance.closeAlert.subscribe(() => this._removeToast(toastrComponentRef));
    instance.doAction.subscribe(() => {
      if (onAction) {
        onAction();
      }
      if (!persistentAction && presentation !== 'modal') {
        this._removeToast(toastrComponentRef);
      }
    });

    // Agregar a la lista de toasts activos
    this._activeToasts.push(toastrComponentRef);

    // Adjuntar al DOM
    const toastContainer =
      document.querySelector('.toast-container') || this._createToastContainer(presentation);
    const toastElement = document.createElement('div');
    toastElement.classList.add('toast-item');
    toastContainer.appendChild(toastElement);
    toastElement.appendChild(toastrComponentRef.location.nativeElement);

    // Adjuntar a Angular para detección de cambios
    this._appRef.attachView(toastrComponentRef.hostView);
  }

  private _createToastContainer(presentation: 'toast' | 'modal' | undefined): HTMLElement {
    // Crear un wrapper para los toasts con posición absoluta y pointer-events habilitados
    const container = document.createElement('div');

    // Aplicar estilos directamente para garantizar que se apliquen correctamente
    if (presentation === 'modal') {
      // Centrado arriba
      container.style.top = '0';
      container.style.left = '50%';
      container.style.right = 'auto';
      container.style.transform = 'translateX(-50%)';
      container.className = 'toast-container w-full h-full';
    } else {
      // Abajo a la derecha
      container.style.top = 'auto';
      container.style.bottom = '1.5rem';
      container.style.right = '1.5rem';
      container.style.left = 'auto';
      container.style.transform = 'none';
      container.style.alignItems = 'flex-end';
      container.style.justifyContent = 'flex-end';
      container.className = 'toast-container';
    }

    // Estilos comunes
    container.style.position = 'fixed';
    container.style.zIndex = '99';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '0.75rem';
    container.style.pointerEvents = 'auto';

    // Inserta el contenedor directamente en el body
    document.body.insertAdjacentElement('beforeend', container);

    return container;
  }

  // Método para eliminar un toast específico
  private _removeToast(toastRef: ComponentRef<CustomToastrComponent>): void {
    // Encontrar el índice del toast en la lista de activos
    const index = this._activeToasts.indexOf(toastRef);
    if (index > -1) {
      // Remover del array
      this._activeToasts.splice(index, 1);

      // Desconectar del detector de cambios de Angular
      this._appRef.detachView(toastRef.hostView);

      // Eliminar del DOM
      const nativeElement = toastRef.location.nativeElement;
      if (nativeElement && nativeElement.parentNode) {
        const toastItem = nativeElement.parentNode;
        toastItem.parentNode.removeChild(toastItem);

        // Si no hay más toasts, eliminar el contenedor
        const container = document.querySelector('.toast-container');
        if (container && !container.hasChildNodes()) {
          container.parentNode?.removeChild(container);
        }
      }
    }
  }

  showSuccess(options: IFeedbackMessage) {
    this._showToast('success', options);
  }

  showError(options: IFeedbackMessage) {
    this._showToast('danger', options);
  }

  showWarning(options: IFeedbackMessage) {
    this._showToast('warning', options);
  }

  showInfo(options: IFeedbackMessage) {
    this._showToast('info', options);
  }

  showConfirm(options: IFeedbackMessage) {
    this._showToast('confirm', options);
  }
}

export interface IFeedbackMessage {
  message?: string;
  title?: string;
  skipTranslation?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  persistentAction?: boolean;
  presentation?: 'toast' | 'modal';
  cancelLabel?: string;
  requestPassword?: boolean;
  typeColor?: 'positive' | 'negative' | 'neutral';
}
